/**
 * CalendlyInlineEmbed
 * Renders the Calendly event type directly as an <iframe> (no widget.js). Listens
 * to Calendly's own postMessage events (origin + source validated) to size the
 * iframe to its content (single scroll: the dialog's) and to detect a booking.
 * If nothing loads within 12 s it shows a prominent "open in a new tab" link; a
 * secondary one is visible from the start. Status, fallback and links sit above
 * the iframe so they stay visible without scrolling and reachable by keyboard
 * before focus enters the cross-origin frame (where Escape can't be observed). After opening the new tab, a
 * "I've booked my call" action lets the person confirm, since no postMessage
 * reaches this page from another tab.
 */

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CALENDLY_LOAD_TIMEOUT_MS,
  parseCalendlyMessage,
} from "../../../lib/trialExtension";
import { Button } from "../UIElements/Button";
import { Icon } from "../UIElements/Icon";

const PROMINENT_LINK_CLASS =
  "bg-primary text-primary-foreground focus-visible:ring-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium shadow hover:bg-[var(--od-button-primary-hover)] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none";
const SECONDARY_LINK_CLASS =
  "text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md px-2 text-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none";

const CalendlyInlineEmbed = ({ embedUrl, fallbackUrl, onScheduled }) => {
  const { t } = useTranslation(["billing"]);
  const iframeRef = useRef(null);
  const scheduledRef = useRef(false);
  const timeoutRef = useRef(null);
  const onScheduledRef = useRef(onScheduled);
  onScheduledRef.current = onScheduled;

  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [height, setHeight] = useState(null);
  const [openedInNewTab, setOpenedInNewTab] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    setHeight(null);
    scheduledRef.current = false;

    timeoutRef.current = window.setTimeout(
      () => setFailed(true),
      CALENDLY_LOAD_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeoutRef.current);
  }, [embedUrl]);

  useEffect(() => {
    const markLoaded = () => {
      setLoaded(true);
      // Calendly may answer after the timeout: drop the failure notice then.
      setFailed(false);
      window.clearTimeout(timeoutRef.current);
    };

    const handleMessage = (event) => {
      const message = parseCalendlyMessage(
        event,
        iframeRef.current?.contentWindow,
      );
      if (!message) return;

      if (message.type === "calendly.page_height") {
        if (message.height) setHeight(message.height);
        markLoaded();
      } else if (message.type === "calendly.event_type_viewed") {
        markLoaded();
      } else if (
        message.type === "calendly.event_scheduled" &&
        !scheduledRef.current
      ) {
        scheduledRef.current = true;
        onScheduledRef.current?.(message.payload);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Left click, middle click (auxclick) or the context menu ("Open link in new
  // tab") all may open the calendar elsewhere; setting true is idempotent.
  const handleOpenedInNewTab = () => setOpenedInNewTab(true);
  const newTabHandlers = {
    onClick: handleOpenedInNewTab,
    onAuxClick: handleOpenedInNewTab,
    onContextMenu: handleOpenedInNewTab,
  };

  return (
    <div className="flex flex-col gap-3">
      {!loaded && !failed && (
        <p
          role="status"
          className="text-muted-foreground text-center text-sm"
          data-testid="trial-extension-calendar-loading"
        >
          {t("trialExtension.calendarLoading")}
        </p>
      )}

      {failed && (
        <div
          role="alert"
          className="border-border rounded-md border p-4 text-center"
        >
          <p className="text-foreground mb-3 text-sm">
            {t("trialExtension.calendarFailed")}
          </p>
          <a
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={PROMINENT_LINK_CLASS}
            {...newTabHandlers}
          >
            <Icon name="ExternalLink" className="h-4 w-4" />
            {t("trialExtension.openNewTabProminent")}
          </a>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-2">
        <a
          href={fallbackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={SECONDARY_LINK_CLASS}
          {...newTabHandlers}
        >
          <Icon name="ExternalLink" className="h-4 w-4" />
          {t("trialExtension.openNewTab")}
        </a>
        {openedInNewTab && (
          <Button
            type="button"
            emphasis="outline"
            intent="neutral"
            size="touch"
            onClick={() => onScheduledRef.current?.(null)}
          >
            {t("trialExtension.confirmBooked")}
          </Button>
        )}
      </div>

      <div
        className="overflow-hidden rounded-md border"
        style={height ? { height: `${height}px` } : undefined}
      >
        <iframe
          ref={iframeRef}
          title={t("trialExtension.calendarIframeTitle")}
          src={embedUrl}
          // Explicitly in the tab order; the dialog's focus guards wrap Tab
          // back into the dialog when it leaves the cross-origin frame.
          tabIndex={0}
          className={`block h-full w-full border-0 ${height ? "min-h-0" : "min-h-[1050px] sm:min-h-[700px]"}`}
        />
      </div>
    </div>
  );
};

export default CalendlyInlineEmbed;
